/**
 * Utilities for the Quick-Reply composer (WhatsApp closed-window flow).
 *
 * Quick-reply templates let agents pick a pre-approved template, type a
 * free-text answer into a single editable slot ({{answer}}), and send the
 * assembled message even when the 24-hour Meta service window is closed.
 */

import { getTemplateBody } from './whatsAppTemplateUtils';

// Convention: quick-reply templates are identified by this name prefix (used as
// a fallback; the primary marker is now usage_type === 'QUICK_REPLY').
export const QUICK_REPLY_PREFIX = 'quick_reply_';

// LOCKED template set: 3 greetings × 6 sign-offs, named
//   quick_reply_<signoff>_<greeting>
// where greeting ∈ {hi, tod, pat} is an EXPLICIT suffix on ALL variants (no more
// "hi = base"). Meta forbids a body starting with a variable, so each greeting
// is its own approved opener:
//   hi  → "Hi {{first_name}},"        (has the patient's name)
//   tod → "Good {{time_of_day}},"     (time of day, no name)
//   pat → "Thanks for your patience," (no name, no time)
// This ordered config is the single source of truth for the greeting selector.
export const GREETINGS = [
    { key: 'hi', emoji: '👋', word: 'Hi', slot: 'name' },
    { key: 'tod', emoji: '☀️', word: 'Good', slot: 'time' },
    { key: 'pat', emoji: '🙏', word: 'Thanks for waiting', slot: null },
];
export const GREETING_KEYS = GREETINGS.map((g) => g.key);
// Default greeting = "Good {time}" (the featured opener).
export const DEFAULT_GREETING = 'tod';

/**
 * Identify a Personal Reply template. Primary signal is the `usage_type`; the
 * `quick_reply_` name prefix is kept as a belt-and-braces fallback during the
 * transition. Used to INCLUDE these in the composer and EXCLUDE them everywhere
 * else (the normal template pickers / admin lists).
 *
 * @param {object} t - a WhatsApp template object
 * @returns {boolean}
 */
export function isQuickReplyTemplate(t) {
    return t?.usage_type === 'QUICK_REPLY' || Boolean(t?.name?.startsWith(QUICK_REPLY_PREFIX));
}

/**
 * Identify templates that belong on ordinary WhatsApp template surfaces.
 * Personal Reply templates are reserved for the closed-window composer and
 * must not be previewed, edited or sent through the normal selector.
 *
 * @param {object} template - a WhatsApp template object
 * @returns {boolean}
 */
export function isNormalWhatsAppTemplate(template) {
    return Boolean(template) && !isQuickReplyTemplate(template);
}

/**
 * Remove Personal Reply templates without mutating the source list. Both the
 * explicit usage type and the legacy name prefix are honoured by the shared
 * predicate above so every caller gets the same transition-safe behaviour.
 *
 * @param {Array} templates - full template list from Redux/API
 * @returns {Array} normal-template subset
 */
export function filterNormalWhatsAppTemplates(templates) {
    if (!Array.isArray(templates)) return [];
    return templates.filter(isNormalWhatsAppTemplate);
}

// Meta caps individual variable values at this length.
export const QUICK_REPLY_ANSWER_MAX_CHARS = 640;

// At or above this many quick-reply templates the picker switches from an
// inline flex-wrap row to a horizontal scroll-snap carousel so it stays tidy.
export const QUICK_REPLY_CAROUSEL_THRESHOLD = 3;

/**
 * Decide whether the template picker should render as a carousel.
 * True once there are >= QUICK_REPLY_CAROUSEL_THRESHOLD templates; below that
 * the inline cards read better.
 *
 * @param {number} count - number of quick-reply templates available
 * @returns {boolean}
 */
export function shouldShowCarousel(count) {
    return Number(count) >= QUICK_REPLY_CAROUSEL_THRESHOLD;
}

/**
 * True when a template exposes an agent-typed answer slot — either via its
 * BODY context-mapping (an "answer" key) or a literal {{answer}} token in the
 * body text. Templates without an answer slot are useless for Personal Reply
 * (the operator's typed answer would be silently dropped at send time), so
 * they must not appear in the picker.
 *
 * @param {object} template - a WhatsApp template object
 * @returns {boolean}
 */
export function templateHasAnswerSlot(template) {
    const bodyMapping = template?.template_context_mapping?.BODY;
    if (Array.isArray(bodyMapping) && bodyMapping.includes('answer')) return true;
    const body = getTemplateBody(template);
    return typeof body === 'string' && body.includes('{{answer}}');
}

// When true, filterQuickReplyTemplates ALSO accepts status 'PENDING' (was a
// temporary local-preview allowance). Now false — the 6 quick_reply_<signoff>[_tod]
// templates are APPROVED, so APPROVED-only is correct.
export const QR_PREVIEW_INCLUDE_PENDING = false;

/**
 * Filter a WhatsApp template list down to the quick-reply subset.
 * Keeps only APPROVED templates that (a) start with QUICK_REPLY_PREFIX AND
 * (b) actually have an answer slot — otherwise the operator would type an
 * answer that is silently ignored.
 *
 * (While QR_PREVIEW_INCLUDE_PENDING is true, PENDING templates are also kept —
 * a temporary local visual-preview allowance only.)
 *
 * @param {Array} templates - full template list from Redux
 * @returns {Array} filtered quick-reply templates
 */
export function filterQuickReplyTemplates(templates) {
    if (!Array.isArray(templates)) return [];
    return templates.filter((t) => {
        if (!isQuickReplyTemplate(t)) return false;
        const status = String(t?.status || '').toUpperCase();
        // Treat missing status as approved so seeded/mock templates work in tests.
        // PENDING is accepted only under the temporary preview flag.
        const statusOk =
            !status || status === 'APPROVED' || (QR_PREVIEW_INCLUDE_PENDING && status === 'PENDING');
        if (!statusOk) return false;
        // Must have an answer slot, or the typed answer goes nowhere.
        return templateHasAnswerSlot(t);
    });
}

/**
 * Build the template_context object that gets posted to the BE alongside
 * the template name. The BE context APIs will also supply first_name /
 * treatment / practice_name at send time; supplying them here means the
 * live preview renders without a round-trip, and the agent-typed answer is
 * delivered correctly regardless of whether the BE lookup returns those keys.
 *
 * @param {object} opts
 * @param {string} opts.firstNameHint   - lead's first name (from leadDetails)
 * @param {string} [opts.lastNameHint]  - lead's last name (from leadDetails)
 * @param {string} opts.treatmentHint   - treatment name (from lead_treatments[0])
 * @param {string} opts.practiceNameHint - practice's display name
 * @param {string} [opts.practicePhoneHint] - practice's contact phone number
 * @param {string} opts.answer           - agent-typed free-text answer
 * @param {string} [opts.timeOfDay]      - PREVIEW ONLY: locally-computed
 *   morning/afternoon/evening used to render the `_tod` variant's
 *   {{time_of_day}} slot in the live preview. Pass this ONLY when building the
 *   preview context — NEVER when building the context sent to the BE. On send
 *   the server's practice get_context supplies the authoritative Europe/London
 *   `time_of_day` and resolves the slot itself, so a client clock supplying it
 *   would be both redundant and wrong for practices in other zones.
 * @returns {object} template_context
 */
export function buildQuickReplyContext({
    firstNameHint = '',
    lastNameHint = '',
    treatmentHint = '',
    practiceNameHint = '',
    practicePhoneHint = '',
    answer = '',
    timeOfDay = '',
} = {}) {
    const context = {
        first_name: firstNameHint,
        treatment_name: treatmentHint,
        practice_name: practiceNameHint,
        answer,
        // Aliases matching the BACKEND `get_context()` field names (see
        // apps.leads.models.Lead.get_context / apps.practice.models.
        // PracticeContact.get_context on the dentalcrm repo). A mapped
        // template can be authored with EITHER the FE's short keys above OR
        // the raw backend names — both must resolve to the SAME underlying
        // value, or the FE pre-send check would block a send the BE would
        // have filled correctly.
        lead_first_name: firstNameHint,
        lead_last_name: lastNameHint,
        practice_phone: practicePhoneHint,
    };
    // Only added for the live preview (see @param note). Omitted from the sent
    // context so the BE remains the single source of truth for time_of_day.
    if (timeOfDay) context.time_of_day = timeOfDay;
    return context;
}

// Keys that carry INTENTIONAL free-text content, where an empty string is a
// real (if unusual) value rather than "we don't know this yet" — e.g. the
// Personal Reply / mapped-template answer slot. stripBlankContextValues never
// drops these, even when blank.
export const CONTEXT_KEYS_NEVER_STRIPPED = ['answer'];

/**
 * Remove blank/null/undefined values from a template_context object before it
 * is sent as `extraContext` to `sendWhatsappMessage` (whatsappV2.js).
 *
 * `buildWhatsappSendContext` spreads `extraContext` LAST, on top of the fresh
 * practice/lead/treatment context `sendWhatsappMessage` fetches at click
 * time — so any key present in extraContext, even as `''`, unconditionally
 * WINS. A hint the FE hasn't loaded yet (e.g. `practiceName` before Redux
 * populates it, or `practicePhone` when the caller doesn't pass one) becomes
 * `''` in `buildQuickReplyContext`'s output; left in, that blank string
 * clobbers the correct value the backend just looked up, and the patient
 * receives a message with the field genuinely empty even though the agent's
 * preview showed it correctly (the preview reads from the full backend
 * snapshot, which the send path never sees). Stripping blanks here means a
 * key extraContext doesn't genuinely know is left for the fresh backend
 * context to fill, instead of overwriting it with nothing.
 *
 * `CONTEXT_KEYS_NEVER_STRIPPED` (currently just `answer`) is exempt: those
 * keys mean actual typed content, and an intentionally blank one should stay
 * blank rather than fall back to whatever the backend would otherwise fill.
 *
 * @param {object} context - a template_context object (e.g. from buildQuickReplyContext)
 * @returns {object} the same object with blank/null/undefined values removed
 */
export function stripBlankContextValues(context) {
    if (!context || typeof context !== 'object') return {};
    return Object.fromEntries(
        Object.entries(context).filter(
            ([key, value]) => CONTEXT_KEYS_NEVER_STRIPPED.includes(key)
                || (value !== '' && value !== null && value !== undefined)
        )
    );
}

/**
 * PREVIEW ONLY. Derive a coarse time-of-day bucket from a local clock so the
 * live preview of a `_tod` greeting variant reads "Good evening, …". This value
 * is never sent — the BE supplies the authoritative Europe/London value on send.
 *
 *   hour < 12 → 'morning' · < 18 → 'afternoon' · otherwise → 'evening'
 *
 * @param {Date} [now] - injectable for testing; defaults to the current time
 * @returns {'morning'|'afternoon'|'evening'}
 */
export function computeQuickReplyTimeOfDay(now = new Date()) {
    const date = now instanceof Date && !Number.isNaN(now.getTime()) ? now : new Date();
    const hour = date.getHours();
    if (hour < 12) return 'morning';
    if (hour < 18) return 'afternoon';
    return 'evening';
}

/**
 * Extract the greeting key (hi/tod/pat) from a template name's trailing
 * `_<greeting>` suffix. Sign-off keys never contain underscores, so the last
 * suffix uniquely identifies the greeting.
 *   quick_reply_more_tod → 'tod'
 *
 * @param {string} name - a template name
 * @returns {string|null} the greeting key, or null when there's no valid suffix
 */
export function getQuickReplyGreeting(name) {
    if (typeof name !== 'string') return null;
    return GREETING_KEYS.find((key) => name.endsWith(`_${key}`)) || null;
}

/**
 * Derive the SIGN-OFF key from a quick-reply template name by stripping the
 * trailing `_<greeting>` suffix AND the `quick_reply_` prefix. All three
 * greeting variants of a sign-off collapse to the same key:
 *   quick_reply_more_hi  → 'more'
 *   quick_reply_more_tod → 'more'
 *   quick_reply_more_pat → 'more'
 *
 * @param {string} name - a template name
 * @returns {string} the sign-off key ('' for non-strings)
 */
export function getQuickReplyPurpose(name) {
    if (typeof name !== 'string') return '';
    let base = name;
    const greeting = getQuickReplyGreeting(base);
    if (greeting) {
        base = base.slice(0, -(`_${greeting}`.length));
    }
    if (base.startsWith(QUICK_REPLY_PREFIX)) {
        base = base.slice(QUICK_REPLY_PREFIX.length);
    }
    return base;
}

/**
 * Group the filtered quick-reply templates by SIGN-OFF, collecting the three
 * greeting variants { hi, tod, pat } (any may be null). Preserves first-seen
 * order. Templates without a recognised greeting suffix are skipped.
 *
 * @param {Array} templates - full template list from Redux
 * @returns {Array<{ purpose: string, hi: object|null, tod: object|null, pat: object|null }>}
 */
export function groupQuickReplyTemplates(templates) {
    const filtered = filterQuickReplyTemplates(templates);
    const groups = [];
    const byPurpose = new Map();
    filtered.forEach((template) => {
        const greeting = getQuickReplyGreeting(template.name);
        if (!greeting) return; // every variant must carry an explicit greeting suffix
        const purpose = getQuickReplyPurpose(template.name);
        let group = byPurpose.get(purpose);
        if (!group) {
            group = { purpose, hi: null, tod: null, pat: null };
            byPurpose.set(purpose, group);
            groups.push(group);
        }
        // First template of each greeting wins (defends against dup names).
        if (!group[greeting]) group[greeting] = template;
    });
    return groups;
}

/**
 * Resolve a sign-off group + greeting key to the concrete template to
 * render/send. Falls back to any available greeting (in GREETINGS order) when
 * the requested greeting has no approved template for this sign-off.
 *
 * @param {{ hi: object|null, tod: object|null, pat: object|null }} group
 * @param {string} greetingKey - one of GREETING_KEYS
 * @returns {object|null} the resolved template
 */
export function resolveQuickReplyVariant(group, greetingKey) {
    if (!group) return null;
    if (greetingKey && group[greetingKey]) return group[greetingKey];
    return GREETING_KEYS.map((key) => group[key]).find(Boolean) || null;
}

// Default greeting preference when the staff pick isn't APPROVED for a sign-off:
// prefer the featured "Good {time}" opener, then "Hi {name}", then "Thanks for
// waiting". (Distinct from GREETING_KEYS display order — this is fallback order.)
export const GREETING_PREFERENCE = ['tod', 'hi', 'pat'];

/**
 * Pick an APPROVED greeting for a sign-off's variant group.
 * Keeps the staff's current pick when it's approved; otherwise falls back to the
 * first approved greeting in preference order (tod → hi → pat). Returns null when
 * the sign-off has NO approved greeting (all variants pending/absent) — the
 * caller should then treat the sign-off as unsendable.
 *
 * A greeting key is present in `group` only for APPROVED variants (the group is
 * built from filterQuickReplyTemplates, which excludes PENDING), so "in group"
 * === "approved" here.
 *
 * @param {object|null} group       - { hi, tod, pat } approved-variant group
 * @param {string}      currentPick - the staff's currently-selected greeting key
 * @returns {string|null} an approved greeting key, or null
 */
export function pickAvailableGreeting(group, currentPick) {
    if (currentPick && group?.[currentPick]) return currentPick;
    return GREETING_PREFERENCE.find((key) => group?.[key]) || null;
}

/**
 * Body for the optimistic "sending" bubble on an approved-template send.
 * Personal Reply passes the typed `answer`; a mapped template has no answer, so
 * the caller passes the rendered draft body as `optimisticContent` — this keeps
 * the in-flight bubble from being blank on a mapped send.
 *
 * @param {string} optimisticContent - explicit body (mapped-template draft)
 * @param {string} answer            - the typed Personal Reply answer
 * @returns {string}
 */
export function pickOptimisticContent(optimisticContent, answer) {
    return optimisticContent || answer || '';
}

/**
 * Whether the closed-window "+"-menu should show the "Personal Reply" item.
 * Hidden while a mapped-template draft is pinned, because Personal Reply is
 * suppressed then (the menu item would be a dead click).
 *
 * @param {boolean}     hasOptions          - personalReply.hasOptions
 * @param {object|null} mappedTemplateDraft
 * @returns {boolean}
 */
export function showPersonalReplyMenuItem(hasOptions, mappedTemplateDraft) {
    return Boolean(hasOptions) && !mappedTemplateDraft;
}

/**
 * Visibility gate for the chat "scroll to bottom" FAB. The FAB sits at a fixed
 * bottom offset, so it must hide while the footer is expanded — the mapped-
 * template draft bar OR the Personal Reply greeting/sign-off chips add rows that
 * make the footer taller than the FAB offset, causing overlap. It reappears once
 * the footer collapses. Pure — no React.
 *
 * @param {object}  args
 * @param {boolean} args.isUserNearBottom     - already scrolled to the bottom
 * @param {boolean} args.isLoading            - chat screen still loading
 * @param {number}  args.messageCount         - whatsappMessages.length
 * @param {object|null} args.mappedTemplateDraft
 * @param {boolean} args.personalReplyActive
 * @returns {boolean}
 */
export function isScrollToBottomFabVisible({
    isUserNearBottom,
    isLoading,
    messageCount,
    mappedTemplateDraft,
    personalReplyActive,
} = {}) {
    return Boolean(
        !isUserNearBottom &&
            !isLoading &&
            messageCount > 0 &&
            !mappedTemplateDraft &&
            !personalReplyActive,
    );
}

/**
 * Find a loaded WhatsApp template for the mapped-template draft flow (checklist
 * WhatsApp item → sidebar / slideout composer).
 *
 * The `/checklist-whatsapp-mappings/resolve/` endpoint returns BOTH the
 * `whatsapp_template_id` (the WhatsappMessageTemplate PK) and its
 * `whatsapp_template_name`. We match against the already-loaded
 * `currentWhatsappTemplate` list (Redux) so we can preview + send via the
 * approved-template path — no per-id fetch, and it works even when the 24-hour
 * window is closed.
 *
 * MATCH BY ID FIRST, exact-name fallback. The `id` is the stable PK and is
 * unambiguous even if two WABAs happen to share a template name; name is only a
 * fallback for the (rare) case the resolve row carries no id. This is safe
 * because `currentWhatsappTemplate` is itself loaded per-WABA — see the loader
 * `whatsapp-business-accounts/{currentBusinessId}/whatsapp-message-templates/`
 * in src/redux/slices/whatsappV2.js (~L1389 sync + ~L1501 init), so the list is
 * already scoped to the active business/phone; both id and name resolve within
 * that single WABA.
 *
 * @param {Array}                templates - the loaded WhatsApp template list
 * @param {string|number|null}   id        - WhatsappMessageTemplate PK (preferred)
 * @param {string|null}          name      - exact Meta template name (fallback)
 * @returns {object|null}                  - the matching template, or null
 */
export function resolveMappedTemplate(templates, id, name) {
    const list = Array.isArray(templates) ? templates : [];
    if (id != null && id !== '') {
        const byId = list.find((t) => t?.id != null && String(t.id) === String(id));
        if (byId) return byId;
    }
    if (name) {
        return list.find((t) => t?.name === name) || null;
    }
    return null;
}

/**
 * Parse a `/checklist-whatsapp-mappings/resolve/` response into { id, name }.
 * Shared by Path A (ChecklistItemRow slideout) and Path B (FollowUpPanel sidebar)
 * so both resolve the mapped WhatsApp template identically.
 *
 * @param {object} res - callApi response ({ data: [{ whatsapp_template_id, whatsapp_template_name }] })
 * @returns {{id: (string|number|null), name: (string|null)}|null} - null when no usable mapping
 */
export function parseWtmResolveResponse(res) {
    const row = res?.data?.[0];
    if (!row) return null;
    const id = row.whatsapp_template_id ?? null;
    const name = row.whatsapp_template_name ?? null;
    if (id == null && !name) return null;
    return { id, name };
}

/**
 * Resolve the WhatsApp sidebar mapped-template state from an onOpenSidebarTab
 * `opts` payload — ALWAYS returns explicit values (string or null).
 *
 * Critical: FollowUpPanel fires onOpenSidebarTab('whatsapp') with NO opts FIRST
 * (on channel select), then fires it again with { templateName, templateWaId }
 * ONLY when a mapping exists. Switching from an item WITH a mapping to one
 * WITHOUT would otherwise leave the previous name/id set (stale) — the operator
 * never dismissed it, so the wrong template would reappear. Returning explicit
 * nulls makes the no-opts call CLEAR the previous mapping.
 *
 * @param {object} [opts] - { templateName?, templateWaId? }
 * @returns {{name: (string|null), waId: (string|null)}}
 */
export function resolveWhatsappSidebarTemplate(opts) {
    return {
        name: opts?.templateName != null ? String(opts.templateName) : null,
        waId: opts?.templateWaId != null ? String(opts.templateWaId) : null,
    };
}

// Context keys the BACKEND fills authoritatively on send — never counted as
// "missing" in the FE blocked-state check even when blank in the local preview.
export const SERVER_FILLED_CONTEXT_KEYS = ['time_of_day'];

/**
 * Detect template variables that CANNOT be resolved from the given merge context
 * — used to block a mapped-template send that would otherwise go out with blank
 * placeholders ("-"). Walks every {{token}} in the body:
 *   • positional {{N}} → mapped via bodyMapping[N-1]; an UNMAPPED position is
 *     unresolvable (reported as the raw `{{N}}`).
 *   • named {{key}}    → resolved directly from the context.
 * A token is "missing" when its resolved context value is null/blank, EXCEPT for
 * server-filled keys (time_of_day) which the BE supplies on send.
 *
 * @param {string} templateBody - the template BODY text
 * @param {object} context      - the merge context that will be sent
 * @param {Array}  bodyMapping  - positional → context-key map (template_context_mapping.BODY)
 * @returns {string[]} - de-duped list of unresolved field keys / token labels ([] = all good)
 */
export function findUnresolvedTemplateVars(templateBody, context, bodyMapping) {
    if (!templateBody) return [];
    const ctx = context || {};
    const mapping = Array.isArray(bodyMapping) ? bodyMapping : [];
    const missing = [];
    const seen = new Set();
    const re = /{{(\w+)}}/g;
    let match = re.exec(templateBody);
    while (match !== null) {
        const token = match[1];
        const key = /^\d+$/.test(token) ? mapping[Number(token) - 1] : token;
        if (!key) {
            // Unmapped positional token — nothing can fill it.
            const label = `{{${token}}}`;
            if (!seen.has(label)) { seen.add(label); missing.push(label); }
        } else if (!SERVER_FILLED_CONTEXT_KEYS.includes(key)) {
            const val = ctx[key];
            if (val == null || String(val).trim() === '') {
                if (!seen.has(key)) { seen.add(key); missing.push(key); }
            }
        }
        match = re.exec(templateBody);
    }
    return missing;
}

// The Personal Reply composer no longer exposes "templates". Staff make two
// picks — a GREETING and a SIGN-OFF — then type their message. A sign-off is
// the fixed closing line of the message; each is backed by a `quick_reply_<key>`
// template purpose (plus its `_tod` greeting variant). This ordered config is
// the single source of truth for which sign-offs the composer shows, in what
// order, and with what staff-facing label. The first entry is the default.
export const SIGN_OFFS = [
    { key: 'more', label: 'Anything else?' },
    { key: 'call', label: 'Offer a call' },
    { key: 'book', label: 'Book in' },
    { key: 'next', label: 'Over to them' },
    { key: 'reassure', label: 'No rush' },
    { key: 'close', label: 'Wrap up' },
];

// The default sign-off = the first configured one ('more').
export const DEFAULT_SIGN_OFF = SIGN_OFFS[0].key;

// Allowlist of purpose keys the composer is permitted to surface. The older
// quick_reply_pricing/availability/general purposes remain APPROVED at Meta but
// are intentionally excluded here so they never reappear in the composer.
export const SIGN_OFF_KEYS = SIGN_OFFS.map((s) => s.key);

/**
 * Build the composer's sign-off options from the template list. Returns ONE
 * entry per configured sign-off (in SIGN_OFFS order), each carrying its label
 * and the resolved `{hi, tod}` template group — or `group: null` when no
 * template exists yet. Rendering every configured sign-off (rather than only
 * the available ones) lets the selector show all three and disable the ones
 * whose templates aren't approved yet, so a half-approved set degrades cleanly.
 *
 * @param {Array} templates - full template list from Redux
 * @returns {Array<{ key: string, label: string, group: object|null }>}
 */
export function getSignOffGroups(templates) {
    const byPurpose = new Map(
        groupQuickReplyTemplates(templates).map((group) => [group.purpose, group])
    );
    return SIGN_OFFS.map(({ key, label }) => ({
        key,
        label,
        group: byPurpose.get(key) || null,
    }));
}

/**
 * Render a template body string by substituting placeholders with values
 * from a context object. Supports BOTH placeholder styles:
 *
 *   1. POSITIONAL `{{N}}` (1-indexed) — the style Meta-approved templates
 *      actually use. Resolved via the template's BODY context-mapping:
 *      `{{N}}` → `bodyMapping[N-1]` (a context key) → `context[key]`.
 *      Example: bodyMapping = ["first_name","treatment_name","answer",
 *      "practice_name"], so `{{1}}` → "first_name" → context.first_name.
 *
 *   2. NAMED `{{key}}` — direct lookup in `context` (fallback / preview of
 *      un-approved local templates that still use named tokens).
 *
 * Tokens that can't be resolved are left intact (e.g. `{{1}}`) so the
 * operator can see what is still unmapped.
 *
 * @param {string} templateBody - raw body from getTemplateBody(template)
 * @param {object} context      - key→value map
 * @param {string[]} [bodyMapping] - template_context_mapping.BODY: an ordered
 *                                    array of context keys, 1 per {{N}} slot
 * @returns {string}
 */
export function renderQuickReplyPreview(templateBody, context, bodyMapping) {
    if (!templateBody) return '';
    const ctx = context || {};
    const mapping = Array.isArray(bodyMapping) ? bodyMapping : [];
    return templateBody.replace(/{{(\w+)}}/g, (_, token) => {
        // Positional token {{N}} → map 1-based index to a context key, then look up.
        if (/^\d+$/.test(token)) {
            const key = mapping[Number(token) - 1];
            if (key && Object.prototype.hasOwnProperty.call(ctx, key)) {
                return ctx[key] || '';
            }
            return `{{${token}}}`;
        }
        // Named token {{key}} → direct context lookup (fallback path).
        if (Object.prototype.hasOwnProperty.call(ctx, token)) {
            return ctx[token] || '';
        }
        return `{{${token}}}`;
    });
}

/**
 * Split the resolved preview into three parts around the operator's ANSWER
 * slot, so the UI can render the typed answer as its own highlighted paragraph
 * sitting between the fixed template intro and outro.
 *
 * The answer token is located either positionally (the index of `answer` in
 * `bodyMapping` → `{{N}}`) or by the named `{{answer}}` token. Everything
 * before/after that single token is resolved with `renderQuickReplyPreview`
 * (so all OTHER merge fields still fill in); the answer value itself is
 * returned separately so the caller can style it.
 *
 * @param {string} templateBody - raw body from getTemplateBody(template)
 * @param {object} context      - key→value map (must include `answer`)
 * @param {string[]} [bodyMapping] - template_context_mapping.BODY
 * @returns {{ before: string, answer: string, after: string, hasAnswerSlot: boolean }}
 */
export function splitQuickReplyPreview(templateBody, context, bodyMapping) {
    if (!templateBody) {
        return { before: '', answer: '', after: '', hasAnswerSlot: false };
    }
    const ctx = context || {};
    const mapping = Array.isArray(bodyMapping) ? bodyMapping : [];

    // Locate the token that represents the answer slot: positional first
    // (Meta-approved templates), then named (local fallback templates).
    let answerToken = null;
    const posIdx = mapping.indexOf('answer');
    if (posIdx !== -1 && templateBody.includes(`{{${posIdx + 1}}}`)) {
        answerToken = `{{${posIdx + 1}}}`;
    } else if (templateBody.includes('{{answer}}')) {
        answerToken = '{{answer}}';
    }

    // No answer slot → nothing to mark; return the whole resolved message.
    if (!answerToken) {
        return {
            before: renderQuickReplyPreview(templateBody, context, bodyMapping),
            answer: '',
            after: '',
            hasAnswerSlot: false,
        };
    }

    const splitAt = templateBody.indexOf(answerToken);
    const beforeRaw = templateBody.slice(0, splitAt);
    const afterRaw = templateBody.slice(splitAt + answerToken.length);
    return {
        before: renderQuickReplyPreview(beforeRaw, context, bodyMapping),
        answer: ctx.answer || '',
        after: renderQuickReplyPreview(afterRaw, context, bodyMapping),
        hasAnswerSlot: true,
    };
}

/**
 * Validate the agent-typed answer.
 *
 * @param {string} answer
 * @returns {string|null} error message, or null when valid
 */
export function validateQuickReplyAnswer(answer) {
    if (!answer || !answer.trim()) return 'Answer is required';
    // C1: Meta rejects template PARAMETER values that contain newlines, tabs, or
    // 4+ consecutive spaces AT SEND TIME (the preview looks fine but the send
    // fails). Block them here so Send stays disabled and the operator sees why.
    if (/[\r\n\t]/.test(answer) || / {4,}/.test(answer)) {
        return 'Answer can’t contain line breaks, tabs, or long gaps — WhatsApp will reject the message';
    }
    if (answer.length > QUICK_REPLY_ANSWER_MAX_CHARS) {
        return `Answer must be ${QUICK_REPLY_ANSWER_MAX_CHARS} characters or fewer`;
    }
    return null;
}
